import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Icon from '../../../../shared/_TBComponents/Icon';
import Modal from '../../../../shared/_TBComponents/Modal';

// Mirrors the 3-tile AddAthleteModal pattern. Tiles navigate via onClick
// (SPA) rather than an <a href> to avoid a full page reload.

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 64px;
  width: 1200px;
  max-width: 100%;
  box-sizing: border-box;
  padding: 64px;
  border-radius: 8px;
  box-shadow: inset 0 0 0 1px #D6D6D6;
  font-family: "Nunito Sans";

  @media screen and (max-width: 767px) {
    gap: 32px;
    padding: 30px;
  }
`;

const HeadingGroup = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 728px;
  text-align: center;
`;

const HeadingTitle = styled.h2`
  margin: 0;
  font-family: "Nunito Sans";
  font-size: 28px;
  font-weight: 700;
  line-height: 1.5;
  color: #444444;
`;

const HeadingSubtitle = styled.p`
  font-family: "Nunito Sans";
  margin: 0;
  font-size: 19px;
  font-weight: 400;
  line-height: 1.5;
  color: #1A1A1A;
`;

const TileGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 40px;
  width: 100%;
  justify-content: center;
  align-items: stretch;

  @media screen and (max-width: 767px) {
    gap: 24px;
  }
`;

const Tile = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  flex: 1 1 0;
  min-width: 315px;
  height: 270px;
  background: white;
  border: 1px solid #EEE;
  border-radius: 8px;
  box-shadow: 0 16px 24px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
  padding: 48px 24px;
  gap: 16px;
  cursor: pointer;
  transition: box-shadow 0.2s, border-color 0.2s;

  &:hover {
    box-shadow: 0 24px 32px 0 rgba(48, 49, 51, 0.15), 0 0 2px 0 rgba(48, 49, 51, 0.08);
    border-color: #77A7FF;
    opacity: 0.7;
  }

  &:focus-visible { outline: 2px solid #1787FB; outline-offset: 2px; }

  @media screen and (max-width: 767px) {
    height: auto;
    min-height: 270px;
  }
`;

const TileText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const TileTitle = styled.span`
  font-family: "Nunito Sans";
  font-size: 19px;
  font-weight: 700;
  line-height: 1.5;
  color: #444444;
`;

const TileDescription = styled.span`
  font-family: "Nunito Sans";
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: #1A1A1A;
`;

const CancelButton = styled.button`
  border: none;
  background: none;
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-family: "Nunito Sans";
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.39px;
  text-transform: uppercase;
  color: #9E9E9E;

  &:hover { color: #444444; }
  &:focus-visible { outline: 2px solid #1787FB; outline-offset: 2px; }
`;

const AddImportProgramModal = ({ isOpen, onClose, options }) => (
  <Modal
    isOpen={isOpen}
    onRequestClose={onClose}
    hideButton
    hideTitle
    customMinHeight='0'
    customMaxWidth='min(1200px, 90vw)'
    customPadding='0px'
    customBorderRadius='8px'
    flexGrow={false}
    widthFitContent
    overlayBackgroundColor='rgba(0, 0, 0, 0.6)'
  >
    <ModalContent>
      <HeadingGroup>
        <HeadingTitle>Add or Import a Program</HeadingTitle>
        <HeadingSubtitle>Choose how you would like to add a new program.</HeadingSubtitle>
      </HeadingGroup>
      <TileGroup>
        {options.map((opt) => (
          <Tile
            key={opt.key}
            type='button'
            onClick={() => {
              onClose();
              opt.onClick();
            }}
          >
            <Icon icon={opt.icon} size={64} color='#77A7FF' />
            <TileText>
              <TileTitle>{opt.title}</TileTitle>
              <TileDescription>{opt.description}</TileDescription>
            </TileText>
          </Tile>
        ))}
      </TileGroup>
      <CancelButton type='button' onClick={onClose}>Cancel</CancelButton>
    </ModalContent>
  </Modal>
);

AddImportProgramModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
  })).isRequired,
};

export default AddImportProgramModal;
